import { nlBE } from 'date-fns/locale';
import {
  formatDate,
  Interval,
  isSameDay,
  NormalizedInterval,
  parseISO,
  setISODay,
} from 'date-fns';
import { getScheduleInterval, Schedule } from '../api/_lib/schedule';
import { Pipe, PipeTransform } from '@angular/core';

export function mutParseInterval(interval: Interval) {
  if (typeof interval.start === 'string') {
    interval.start = parseISO(interval.start as string);
  }
  if (typeof interval.end === 'string') {
    interval.end = parseISO(interval.end as string);
  }
}

export const DATE_FNS_LOCALE = nlBE;

export interface DateInterval extends Interval {
  reason?: string;
}

export interface NormalizedDateInterval extends NormalizedInterval {
  reason?: string;
}

export function formatDayMonth(date: Date): string {
  return formatDate(date, 'dd/MM', { locale: DATE_FNS_LOCALE });
}

export function formatTablePeriodDate(data: { from?: Date; to?: Date }) {
  const from = data.from
    ? `${formatDate(data.from, 'dd/MM/yyyy', { locale: DATE_FNS_LOCALE })}`
    : '';
  const to = data.to
    ? `${formatDate(data.to, 'dd/MM/yyyy', { locale: DATE_FNS_LOCALE })}`
    : '';
  return `${from} ${from && to ? ' - ' : ''} ${to}`;
}

export function extractDateLabel(date?: Date) {
  if (!date) return '---';

  return formatDate(date, 'dd/MM/yyyy', { locale: DATE_FNS_LOCALE });
}

export function extractFullDateLabel(date?: string | number | Date) {
  if (!date) return '---';

  return formatDate(date, 'eeee dd/MM/yyyy', { locale: DATE_FNS_LOCALE });
}

export function extractTimeLabel(date?: string | number | Date) {
  if (!date) return '---';

  return formatDate(date, 'HH:mm', { locale: DATE_FNS_LOCALE });
}

export function extractDateTimeLabel(date?: Date) {
  if (!date) return '---';

  return formatDate(date, 'dd/MM/yyyy HH:mm:ss', { locale: DATE_FNS_LOCALE });
}

export function extractPeriodLabel(
  schedule: Schedule | null | undefined,
): string {
  if (!schedule) return '---';
  const { start, end } = getScheduleInterval(schedule);

  return `${formatDate(start, 'dd/MM/yyyy', { locale: DATE_FNS_LOCALE })} - ${formatDate(end, 'dd/MM/yyyy', { locale: DATE_FNS_LOCALE })}`;
}

export function extractWeekday(
  value: Date | string | number | null | undefined,
  full: boolean = false,
): string {
  if (!value) return '---';
  if (typeof value === 'number' && Number.isInteger(value)) {
    value = setISODay(new Date(), value);
  }
  return formatTableDate(value, full ? 'EEEE' : 'EEE');
}

export function formatTableDate(
  value?: Date | string | number,
  format = 'dd/MM/yyyy',
): string {
  if (!value) return '---';

  return formatDate(value, format, { locale: DATE_FNS_LOCALE });
}

@Pipe({
  name: 'formatTableDate',
  standalone: true,
})
export class FormatTableDatePipe implements PipeTransform {
  transform(
    value?: Date | string | number | null,
    format = 'dd/MM/yyyy',
  ): string {
    if (!value) return '---';

    return formatTableDate(value, format);
  }
}

@Pipe({
  name: 'weekday',
  standalone: true,
})
export class WeekdayPipe implements PipeTransform {
  transform(
    value: Date | string | number | null | undefined,
    full: boolean = false,
  ): string {
    return extractWeekday(value, full);
  }
}

@Pipe({
  name: 'isIntervalOverlapWithDay',
  standalone: true,
})
export class IsIntervalOverlapWithDayPipe implements PipeTransform {
  transform(intervals: DateInterval[], date: string | number | Date): boolean {
    return intervals.some(({ start }) => isSameDay(start, date));
  }
}
